import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import {
  getGreenOutcomeValueId,
  getNaOutcomeValueId,
} from "@repo/app-types/entities/outcome";
import {
  getOutcomeDefinitionFromPlaybook,
  type AdminApiMinimalPlaybook,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import type { SignalOutcomeBreakdownByPlaybook } from "@repo/app-types/entities/signal";
import { outcomeColorSchemeMap } from "@repo/ui/Charts/analyticsColors";
import { StackedBar } from "@repo/ui/Charts/stackedBar";
import { useMemo, useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { BaseCard } from "../../performanceReports/baseCard.js";
import {
  computeGreenRate,
  resolveSegments,
  toBarSegments,
} from "./signalOutcomeStats.js";

const OutcomeBreakdownContent: FC<{
  data: SignalOutcomeBreakdownByPlaybook;
  outcomeDef: OutcomeDefinition;
  filterLabels: readonly string[];
}> = ({ data, outcomeDef, filterLabels }) => {
  const [excludeNa, setExcludeNa] = useState(false);

  const naValue = useMemo(() => getNaOutcomeValueId(outcomeDef), [outcomeDef]);
  const greenValue = useMemo(
    () => getGreenOutcomeValueId(outcomeDef),
    [outcomeDef],
  );
  const hasNaSegment = useMemo(() => {
    const allSegments = [
      ...resolveSegments(data.withMatch, outcomeDef),
      ...resolveSegments(data.withoutMatch, outcomeDef),
    ];
    return allSegments.some((s) => s.value === naValue);
  }, [data.withMatch, data.withoutMatch, outcomeDef, naValue]);

  const withSegments = useMemo(() => {
    const segments = resolveSegments(data.withMatch, outcomeDef);
    return excludeNa ? segments.filter((s) => s.value !== naValue) : segments;
  }, [data.withMatch, outcomeDef, excludeNa, naValue]);

  const withoutSegments = useMemo(() => {
    const segments = resolveSegments(data.withoutMatch, outcomeDef);
    return excludeNa ? segments.filter((s) => s.value !== naValue) : segments;
  }, [data.withoutMatch, outcomeDef, excludeNa, naValue]);

  const withTotal = withSegments.reduce((s, seg) => s + seg.count, 0);
  const withoutTotal = withoutSegments.reduce((s, seg) => s + seg.count, 0);

  if (withTotal === 0 && withoutTotal === 0) {
    return <NoOutcomeDataPlaceholder />;
  }

  const legendItems = outcomeDef.results
    .filter((r) => !(excludeNa && r.value === naValue))
    .map((r) => ({
      label: r.label,
      color: outcomeColorSchemeMap[r.category]?.dot ?? "#d1d5db",
    }));

  // Compute delta on the green (positive) outcome rate
  const withGreenRate = computeGreenRate(withSegments, outcomeDef);
  const withoutGreenRate = computeGreenRate(withoutSegments, outcomeDef);
  const greenResult = outcomeDef.results.find((r) => r.value === greenValue);

  const hasFilters = filterLabels.length > 0;
  const filterSummary = new Intl.ListFormat("en", {
    style: "long",
    type: "conjunction",
  }).format(filterLabels);
  const withLabel = hasFilters ? `With ${filterSummary}` : "With signal";
  const presenceVerb = filterLabels.length > 1 ? "are" : "is";
  const presenceClause = `when ${hasFilters ? filterSummary : "signal"} ${presenceVerb} present`;

  let deltaText: string | null = null;
  if (withGreenRate !== null && withoutGreenRate !== null && greenResult) {
    const deltaPp = Math.round((withGreenRate - withoutGreenRate) * 100);
    const withPct = Math.round(withGreenRate * 100);
    const withoutPct = Math.round(withoutGreenRate * 100);
    const sign = deltaPp >= 0 ? "+" : "";
    if (deltaPp === 0) {
      deltaText = `${sign}${deltaPp}pp: ${greenResult.label} rate stays level at ${withPct}% ${presenceClause}`;
    } else {
      deltaText = `${sign}${deltaPp}pp: ${greenResult.label} rate ${deltaPp > 0 ? "rises" : "drops"} from ${withoutPct}% to ${withPct}% ${presenceClause}`;
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          {withTotal} matched calls &middot; {withTotal + withoutTotal} total
          calls
        </span>

        {hasNaSegment ? (
          <button
            type="button"
            role="switch"
            aria-checked={excludeNa}
            onClick={() => setExcludeNa((v) => !v)}
            className="flex items-center gap-1.5"
          >
            <span className="text-xs text-muted-foreground">Exclude N/A</span>
            <div
              className={twMerge(
                "relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full transition-colors",
                excludeNa ? "bg-blue-600" : "bg-gray-300",
              )}
            >
              <span
                className={twMerge(
                  "pointer-events-none inline-block h-3 w-3 translate-y-0.5 rounded-full bg-white shadow-sm transition-transform",
                  excludeNa ? "translate-x-3.5" : "translate-x-0.5",
                )}
              />
            </div>
          </button>
        ) : null}
      </div>

      <div className="flex gap-4">
        {legendItems.map((item) => (
          <div key={item.label} className="flex items-center gap-1.5">
            <div
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-xs text-gray-600">{item.label}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <span className="w-28 shrink-0 text-sm text-foreground">
          {withLabel}
        </span>
        <StackedBar
          segments={toBarSegments(withSegments, withTotal)}
          className="flex-1"
        />
        <span className="w-12 shrink-0 text-right text-xs text-muted-foreground">
          n={withTotal}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span className="w-28 shrink-0 text-sm text-foreground">
          All other calls
        </span>
        <StackedBar
          segments={toBarSegments(withoutSegments, withoutTotal)}
          className="flex-1"
        />
        <span className="w-12 shrink-0 text-right text-xs text-muted-foreground">
          n={withoutTotal}
        </span>
      </div>

      {deltaText && <p className="text-sm text-gray-600">{deltaText}</p>}
    </div>
  );
};

export const SignalOutcomeBreakdown: FC<{
  outcomeBreakdown: readonly SignalOutcomeBreakdownByPlaybook[];
  playbooks: readonly AdminApiMinimalPlaybook[] | undefined;
  playbookId: PlaybookId;
  filterLabels: readonly string[];
}> = ({ outcomeBreakdown, playbooks, playbookId, filterLabels }) => {
  const pbData = outcomeBreakdown.find((pb) => pb.playbookId === playbookId);
  const playbook = playbooks?.find((p) => p.id === playbookId);
  const outcomeDef = getOutcomeDefinitionFromPlaybook(playbook);
  if (!outcomeDef) return null;

  const title = pbData?.playbookName ?? playbook?.name ?? "Outcome breakdown";

  return (
    <BaseCard title={title}>
      {pbData ? (
        <OutcomeBreakdownContent
          data={pbData}
          outcomeDef={outcomeDef}
          filterLabels={filterLabels}
        />
      ) : (
        <NoOutcomeDataPlaceholder />
      )}
    </BaseCard>
  );
};

const NoOutcomeDataPlaceholder: FC = () => (
  <div className="py-8 text-center">
    <p className="text-sm text-gray-600">No outcome data in this range.</p>
    <p className="mt-1 text-xs text-muted-foreground">
      Try adjusting filters or the date range.
    </p>
  </div>
);
